/**
 * Currents API utility module.
 *
 * Authentication: API key passed via Authorization header.
 * See: https://currentsapi.services/en/docs/
 *
 * Rate limits (free tier):
 *   - 1,000 requests per day
 *   - Max 20 results per request
 *
 * Error codes:
 *   - 401: Unauthorized (invalid or missing API key)
 *   - 429: Rate limit exceeded (daily quota hit)
 *
 * @see https://currentsapi.services/en/docs/
 * @see https://currentsapi.services/en/pricing
 */

const BASE_URL = 'https://api.currentsapi.services/v1';
const API_KEY = process.env.REACT_APP_CURRENTS_API_KEY;

/** In-memory session cache for API responses (5 minute TTL). */
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;

const getCached = (key) => {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL) {
    _cache.delete(key);
    return null;
  }
  return entry.data;
};

const setCache = (key, data) => {
  _cache.set(key, { data, ts: Date.now() });
};

export const clearCache = () => _cache.clear();

let _quota = { remaining: null, limit: null };

export const getQuota = () => ({ ..._quota });

const updateQuota = (response) => {
  const remaining = response.headers.get('X-RateLimit-Remaining');
  const limit = response.headers.get('X-RateLimit-Limit');
  if (remaining !== null) _quota.remaining = Number(remaining);
  if (limit !== null) _quota.limit = Number(limit);
};

/**
 * Build base URLSearchParams with common params (no auth).
 * Auth is via Authorization header, not query parameter.
 */
const buildParams = (extras = {}) => {
  return new URLSearchParams(extras);
};

/**
 * Fetch with Authorization header (raw API key, no Bearer prefix).
 * Per Currents Swagger spec: auth is via "Authorization" header.
 *
 * @param {string} url - Full URL with query params.
 * @returns {Promise<Response>}
 */
const fetchWithAuth = (url) => {
  return fetch(url, {
    headers: { 'Authorization': API_KEY },
  });
};

const handleApiError = async (response) => {
  const { status, statusText } = response;

  let body = '';
  try {
    body = await response.text();
  } catch {
    // ignore
  }

  console.error(`[API] ${status} ${statusText}`, body);

  if (status === 401) {
    throw new Error('Unable to connect to news service. Please try again later.');
  }
  if (status === 429) {
    clearCache();
    throw new Error(
      'rate_limit'
    );
  }
  throw new Error('Unable to load news. Please try again later.');
};

const validateApiKey = () => {
  if (!API_KEY) {
    throw new Error(
      'API key not configured. Copy .env.example to .env and add your Currents API key.\n' +
      'Get a free key at: https://currentsapi.services/en/register'
    );
  }
};

/**
 * Fetch the latest news headlines.
 * Note: /latest-news does NOT support category filtering.
 *
 * @param {string} [language='en']
 * @param {number} [pageSize=10]
 * @param {string|null} [pageToken=null]
 */
export const fetchLatestNews = async (language = 'en', pageSize = 10, pageToken = null) => {
  validateApiKey();

  if (!pageToken) {
    const cacheKey = `latest:${language}:${pageSize}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const params = buildParams({ language, page_size: String(pageSize) });
  if (pageToken) params.set('page_token', pageToken);

  const response = await fetchWithAuth(`${BASE_URL}/latest-news?${params}`);

  updateQuota(response);
  if (!response.ok) await handleApiError(response);

  const data = await response.json();
  const result = {
    articles: data.news || [],
    nextPageToken: data.nextPageToken || null,
    hasMore: !!data.nextPageToken,
  };

  if (!pageToken) setCache(`latest:${language}:${pageSize}`, result);
  return result;
};

/**
 * Fetch news filtered by category via /search endpoint.
 *
 * @param {string} category
 * @param {number} [pageSize=10]
 * @param {string|null} [pageToken=null]
 */
export const fetchCategoryNews = async (category, pageSize = 10, pageToken = null) => {
  validateApiKey();

  if (!pageToken) {
    const cacheKey = `cat:${category}:${pageSize}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const params = buildParams({ category, language: 'en', page_size: String(pageSize) });
  if (pageToken) params.set('page_token', pageToken);

  const response = await fetchWithAuth(`${BASE_URL}/search?${params}`);

  updateQuota(response);
  if (!response.ok) await handleApiError(response);

  const data = await response.json();
  const result = {
    articles: data.news || [],
    nextPageToken: data.nextPageToken || null,
    hasMore: !!data.nextPageToken,
  };

  if (!pageToken) setCache(`cat:${category}:${pageSize}`, result);
  return result;
};

/**
 * Search news by keyword.
 *
 * @param {string} query
 * @param {number} [pageSize=10]
 * @param {string|null} [pageToken=null]
 */
export const searchNews = async (query, pageSize = 10, pageToken = null) => {
  validateApiKey();

  if (!pageToken) {
    const cacheKey = `search:${query}:${pageSize}`;
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const params = buildParams({ keywords: query, language: 'en', page_size: String(pageSize) });
  if (pageToken) params.set('page_token', pageToken);

  const response = await fetchWithAuth(`${BASE_URL}/search?${params}`);

  updateQuota(response);
  if (!response.ok) await handleApiError(response);

  const data = await response.json();
  const result = {
    articles: data.news || [],
    nextPageToken: data.nextPageToken || null,
    hasMore: !!data.nextPageToken,
  };

  if (!pageToken) setCache(`search:${query}:${pageSize}`, result);
  return result;
};

/**
 * Normalize a raw API article into a consistent shape.
 * Handles edge cases: "None" strings, missing fields, array categories.
 *
 * @param {Object} article
 * @returns {Object}
 */
export const normalizeArticle = (article) => {
  const rawImage = article.image;
  const hasValidImage =
    rawImage && rawImage !== 'None' && rawImage !== 'null' && rawImage.trim() !== '';

  return {
    title: article.title || 'Untitled',
    description: article.description || '',
    url: article.url || '#',
    image: hasValidImage ? rawImage : null,
    author: article.author || 'Unknown',
    date: article.published || new Date().toISOString(),
    source: article.source?.name || article.author || 'Unknown',
    category: Array.isArray(article.category)
      ? article.category
      : [article.category || 'general'],
  };
};

/** Supported news categories from the Currents API. */
export const CATEGORIES = [
  { id: 'general', label: 'General', nav: 'core' },
  { id: 'business', label: 'Business', nav: 'core' },
  { id: 'entertainment', label: 'Entertainment', nav: 'core' },
  { id: 'health', label: 'Health', nav: 'core' },
  { id: 'science', label: 'Science', nav: 'core' },
  { id: 'sports', label: 'Sports', nav: 'core' },
  { id: 'technology', label: 'Technology', nav: 'core' },
  { id: 'world', label: 'World', nav: 'more' },
  { id: 'lifestyle', label: 'Lifestyle', nav: 'more' },
  { id: 'programming', label: 'Programming', nav: 'more' },
  { id: 'finance', label: 'Finance', nav: 'more' },
  { id: 'politics', label: 'Politics', nav: 'more' },
  { id: 'opinion', label: 'Opinion', nav: 'more' },
  { id: 'food', label: 'Food', nav: 'more' },
  { id: 'game', label: 'Game', nav: 'more' },
  { id: 'regional', label: 'Regional', nav: 'more' },
  { id: 'academia', label: 'Academia', nav: 'more' },
];

export const CORE_CATEGORIES = CATEGORIES.filter((c) => c.nav === 'core');
export const MORE_CATEGORIES = CATEGORIES.filter((c) => c.nav === 'more');
